/**
 * Server-side sanity bounds per game. Intentionally light (IDEA.md "Non-goals":
 * not building serious anti-cheat) — reject only physically impossible values,
 * rate-limit elsewhere, accept leaderboards are gameable.
 *
 * Every new game MUST add an entry here (definition-of-done #5).
 */

export type SortDir = "asc" | "desc";

export interface GameSpec {
  /** Inclusive lower bound for a submitted value. */
  min: number;
  /** Inclusive upper bound. */
  max: number;
  /** Leaderboard ordering: "asc" = lower is better (ms), "desc" = higher is better (score). */
  dir: SortDir;
}

export const GAMES = {
  // Reaction-time test: 1ms..5s. We allow lucky sub-100ms taps (luck is luck) — only
  // 0/negative is rejected, which can't happen legitimately anyway (an early tap is
  // caught client-side as "too soon" and never submitted).
  reflex: { min: 1, max: 5_000, dir: "asc" },
  // Aim trainer: targets hit in a fixed window. 0..1000 hard ceiling.
  aim: { min: 0, max: 1_000, dir: "desc" },
  // Daily wheel streak in days. 1 year ceiling.
  wheel: { min: 0, max: 365, dir: "desc" },
  // Crash: cashed-out multiplier ×100 (1.00x..1000.00x). Server-validated against the
  // round's real crash point, so the bounds are just a sanity ceiling.
  crash: { min: 100, max: 100_000, dir: "desc" },
  // Stop-at-5.00s: |released − 5000| in ms. 0 = frame-perfect; past 5s the timer caps
  // the round, so the delta can never legitimately exceed 5000.
  stop: { min: 0, max: 5_000, dir: "asc" },
  // Mines: cashed multiplier ×100. Server-resolved from the round ledger, so bounds
  // are just a sanity ceiling (full 21-gem clear caps at 1000x).
  mines: { min: 100, max: 100_000, dir: "desc" },
  // Limbo: the CALLED target ×100 on a winning roll. Server rolls; bounds = the
  // playable target range (1.01x .. 1000x).
  limbo: { min: 101, max: 100_000, dir: "desc" },
  // Plinko: landed multiplier ×100. Only ≥1.00x buckets score; the 175x edge = 17500.
  plinko: { min: 100, max: 17_500, dir: "desc" },
  // Draw: current call streak written on every hit. A year of daily hits = the cap.
  draw: { min: 1, max: 365, dir: "desc" },
  // Rocket: shared-flight cashout ×100, same ceiling as crash.
  rocket: { min: 100, max: 100_000, dir: "desc" },
} as const satisfies Record<string, GameSpec>;

export type GameId = keyof typeof GAMES;

export function isGameId(value: string): value is GameId {
  return Object.prototype.hasOwnProperty.call(GAMES, value);
}

export function gameSpec(game: GameId): GameSpec {
  return GAMES[game];
}

/** True if `value` is a finite integer within the game's sane bounds. */
export function isValidValue(game: GameId, value: number): boolean {
  if (!Number.isInteger(value)) return false;
  const { min, max } = GAMES[game];
  return value >= min && value <= max;
}

/** Trim + bound a chosen handle. No auth, so this is the only guard on display names. */
export function sanitizeHandle(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const h = raw.trim().replace(/\s+/g, " ");
  if (h.length < 1 || h.length > 24) return null;
  return h;
}
